import { useAtomValue, useSetAtom, useAtom } from "jotai"
import { useEffect } from "react"
import {
  domainLv1Atom,
  domainLv1LoadingAtom,
  domainLv1ErrorAtom,
  domainLv1RefreshCounterAtom,
  refreshDomainLv1Atom,
  domainLv1InitializedAtom,
} from "@/store/domainLv1Atom"
import domainLv1Api from "@/pages/domain/domain-lv1/api"

/**
 * 使用一级领域数据的 Hook
 * 全局共享，使用 Jotai 管理状态
 */
export function useDomainLv1() {
  // 获取 atom 的值
  const [domainLv1s, setDomainLv1s] = useAtom(domainLv1Atom)
  const [isLoading, setIsLoading] = useAtom(domainLv1LoadingAtom)
  const [error, setError] = useAtom(domainLv1ErrorAtom)
  const [initialized, setInitialized] = useAtom(domainLv1InitializedAtom)
  const refreshCounter = useAtomValue(domainLv1RefreshCounterAtom)
  const refreshDomainLv1s = useSetAtom(refreshDomainLv1Atom)

  // 首次加载数据
  useEffect(() => {
    let cancelled = false

    const loadData = async () => {
      // 标记已开始初始化，防止其他组件并发请求
      setInitialized(true)
      setIsLoading(true)
      setError(null)

      try {
        const data = await domainLv1Api.getList()
        if (!cancelled) {
          setDomainLv1s(data.data || [])
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
          // 如果加载失败，重置初始化标记，允许重试
          setInitialized(false)
        }
      }
    }

    // 只有在数据为空且未初始化时才加载
    // 使用 initialized atom 确保全局只发起一次请求
    if (domainLv1s.length === 0 && !initialized) {
      loadData()
    }

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 当刷新计数器变化时，重新加载数据
  useEffect(() => {
    if (refreshCounter === 0) return // 初始值，不需要加载

    let cancelled = false

    const loadData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await domainLv1Api.getList()
        if (!cancelled) {
          setDomainLv1s(data.data || [])
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      }
    }

    loadData()

    return () => {
      cancelled = true
    }
  }, [refreshCounter, setDomainLv1s, setIsLoading, setError])

  /**
   * 使一级领域数据失效，触发重新获取
   */
  const invalidate = () => {
    refreshDomainLv1s()
  }

  return {
    domainLv1s,
    isLoading,
    error,
    refetch: invalidate,
    invalidate,
  }
}
